import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Button, Input, Space, Tag, Modal, Form, Select, message,
  Typography, Row, Col, Switch, Popconfirm
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  TeamOutlined, ReloadOutlined, KeyOutlined, UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import { MobileDataList } from '../../../../components/mobile';
import superAdminApi, { AdminUser, AdminCompany, PaginationParams } from '../../api/superAdminApi';

const { Title, Text } = Typography;
const { Option } = Select;

const ROLES = ['USER', 'ADMIN', 'MANAGER'];

const GlobalUserManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [companies, setCompanies] = useState<AdminCompany[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filterCompany, setFilterCompany] = useState<string | undefined>();
  const [filterRole, setFilterRole] = useState<string | undefined>();
  const [modalVisible, setModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const fetchUsers = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getUsers({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        search: searchText || undefined,
        companyId: filterCompany,
        role: filterRole,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setUsers(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchText, filterCompany, filterRole, pagination.page, pagination.limit]);

  const fetchCompanies = async () => {
    try {
      const response = await superAdminApi.getCompanies({ limit: 100 });
      if (response.success) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const handleSearch = () => {
    fetchUsers({ page: 1 });
  };

  const handleTableChange = (paginationConfig: any) => {
    fetchUsers({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  const handleCreate = () => {
    setSelectedUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: AdminUser) => {
    setSelectedUser(record);
    form.setFieldsValue({
      ...record,
      companyId: record.companyId || undefined
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (selectedUser) {
        const { password, ...updateData } = values;
        await superAdminApi.updateUser(selectedUser.id, updateData);
        message.success('User updated successfully');
      } else {
        await superAdminApi.createUser(values);
        message.success('User created successfully');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Operation failed');
    }
  };

  const handleResetPassword = (record: AdminUser) => {
    setSelectedUser(record);
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handlePasswordSubmit = async (values: any) => {
    if (!selectedUser) return;
    try {
      await superAdminApi.resetUserPassword(selectedUser.id, values.newPassword);
      message.success('Password reset successfully');
      setPasswordModalVisible(false);
    } catch (error: any) {
      message.error(error.message || 'Failed to reset password');
    }
  };

  const handleDelete = async (record: AdminUser) => {
    try {
      await superAdminApi.deleteUser(record.id);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (record: AdminUser) => {
    try {
      await superAdminApi.updateUser(record.id, { isActive: !record.isActive });
      message.success(`User ${record.isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update status');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'purple';
      case 'ADMIN': return 'red';
      case 'MANAGER': return 'blue';
      default: return 'green';
    }
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name || record.email}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </Space>
      )
    },
    {
      title: 'Company',
      key: 'company',
      render: (_, record) => record.companyName ? (
        <Space direction="vertical" size={0}>
          <Text>{record.companyName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.companyCode}</Text>
        </Space>
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => <Tag color={getRoleColor(role)}>{role}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          disabled={record.role === 'SUPER_ADMIN'}
        />
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record)}
          />
          <Popconfirm
            title="Delete this user?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            disabled={record.role === 'SUPER_ADMIN'}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              danger
              disabled={record.role === 'SUPER_ADMIN'}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'name' as const,
    secondaryField: 'email' as const,
    statusField: 'isActive' as const,
    statusConfig: {
      true: { color: 'green', text: 'Active' },
      false: { color: 'red', text: 'Inactive' },
    },
    avatar: { icon: <UserOutlined /> },
    fields: [
      { key: 'role' as const, label: 'Role', render: (role: string) => <Tag color={getRoleColor(role)}>{role}</Tag> },
      { key: 'companyName' as const, label: 'Company', render: (v: string) => v || '-' },
    ],
    onClick: (user: AdminUser) => handleEdit(user),
  };

  // Mobile filters
  const mobileFilters = [
    {
      key: 'role',
      placeholder: 'Role',
      options: [{ label: 'All', value: '' }, ...ROLES.map(r => ({ label: r, value: r }))],
      value: filterRole || '',
      onChange: (v: string) => {
        setFilterRole(v || undefined);
        fetchUsers({ page: 1 });
      },
    },
    {
      key: 'company',
      placeholder: 'Company',
      options: [{ label: 'All', value: '' }, ...companies.map(c => ({ label: c.name, value: c.id }))],
      value: filterCompany || '',
      onChange: (v: string) => {
        setFilterCompany(v || undefined);
        fetchUsers({ page: 1 });
      },
    },
  ];

  // Mobile view
  if (isMobile) {
    return (
      <div>
        {/* Mobile Header */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <Title level={4} style={{ margin: 0 }}>
            <TeamOutlined /> Users
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchUsers()} loading={loading} />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add
            </Button>
          </Space>
        </div>

        {/* Mobile Data List */}
        <MobileDataList
          data={users}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No users found"
          searchPlaceholder="Search users..."
          onSearch={(value) => {
            setSearchText(value);
            fetchUsers({ page: 1 });
          }}
          filters={mobileFilters}
          showFilters={true}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => {
              fetchUsers({ page, limit: pageSize });
            },
          }}
          swipeActions={{
            left: {
              icon: <DeleteOutlined />,
              color: '#ff4d4f',
              label: 'Delete',
              onClick: (user) => {
                if (user.role !== 'SUPER_ADMIN') {
                  handleDelete(user);
                }
              },
            },
            right: {
              icon: <KeyOutlined />,
              color: '#722ed1',
              label: 'Password',
              onClick: (user) => handleResetPassword(user),
            },
          }}
        />

        {/* Create/Edit Modal - Full screen on mobile */}
        <Modal
          title={selectedUser ? 'Edit User' : 'Create User'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width="100%"
          style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Invalid email' }
              ]}
            >
              <Input placeholder="Enter email" disabled={!!selectedUser} />
            </Form.Item>
            <Form.Item name="name" label="Name">
              <Input placeholder="Enter name" />
            </Form.Item>
            {!selectedUser && (
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 8, message: 'Password must be at least 8 characters' }
                ]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            )}
            <Form.Item name="role" label="Role" initialValue="USER">
              <Select>
                {ROLES.map(r => (
                  <Option key={r} value={r}>{r}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="companyId" label="Company">
              <Select placeholder="Select company" allowClear>
                {companies.map(c => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        {/* Password Reset Modal */}
        <Modal
          title={`Reset Password`}
          open={passwordModalVisible}
          onCancel={() => setPasswordModalVisible(false)}
          onOk={() => passwordForm.submit()}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            For: {selectedUser?.email}
          </Text>
          <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit}>
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please enter new password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
            >
              <Input.Password placeholder="Enter new password" />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  }
                })
              ]}
            >
              <Input.Password placeholder="Confirm new password" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <TeamOutlined /> User Management
          </Title>
        </Col>
        <Col>
          <Space>
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
            <Select
              placeholder="Filter by company"
              style={{ width: 180 }}
              allowClear
              value={filterCompany}
              onChange={(v) => {
                setFilterCompany(v);
                fetchUsers({ page: 1 });
              }}
            >
              {companies.map(c => (
                <Option key={c.id} value={c.id}>{c.name}</Option>
              ))}
            </Select>
            <Select
              placeholder="Filter by role"
              style={{ width: 140 }}
              allowClear
              value={filterRole}
              onChange={(v) => {
                setFilterRole(v);
                fetchUsers({ page: 1 });
              }}
            >
              {ROLES.map(r => (
                <Option key={r} value={r}>{r}</Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => fetchUsers()}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Add User
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users`
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedUser ? 'Edit User' : 'Create User'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Invalid email' }
                ]}
              >
                <Input placeholder="Enter email" disabled={!!selectedUser} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="Name">
                <Input placeholder="Enter name" />
              </Form.Item>
            </Col>
          </Row>
          {!selectedUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 8, message: 'Password must be at least 8 characters' }
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="role" label="Role" initialValue="USER">
                <Select>
                  {ROLES.map(r => (
                    <Option key={r} value={r}>{r}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="companyId" label="Company">
                <Select placeholder="Select company" allowClear>
                  {companies.map(c => (
                    <Option key={c.id} value={c.id}>{c.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        title={`Reset Password for ${selectedUser?.email}`}
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        onOk={() => passwordForm.submit()}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordSubmit}>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                }
              })
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GlobalUserManagement;
